import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../lib/firebase";
import Card from "../../components/Card";
import { SubscriptionTier } from "../../types/payment";
import { Mail, ArrowLeft, Home, Crown, Zap, Sparkles, HardDrive, Package, FileText, Plus, Minus, ToggleLeft, ToggleRight } from "lucide-react";

export default function UserManagement() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>("pro_monthly");
  const [username, setUsername] = useState("");
  const [userInfo, setUserInfo] = useState<any>(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeMessage, setEmailChangeMessage] = useState("");
  const [emailChangeError, setEmailChangeError] = useState("");
  
  // Add-on Storage states
  const [addonStorageGB, setAddonStorageGB] = useState(0);
  const [addonActive, setAddonActive] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);
  const [addonMessage, setAddonMessage] = useState("");
  
  // Salepage states
  const [salePageEnabled, setSalePageEnabled] = useState(false);
  const [salePageLoading, setSalePageLoading] = useState(false);
  const [salePageMessage, setSalePageMessage] = useState("");

  // Pre-fill email from URL parameter
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
      // Fetch user info when email is provided
      fetchUserInfo(emailParam);
    }
  }, [searchParams]);

  const fetchUserInfo = async (userEmail: string) => {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", userEmail.trim()));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setUserInfo(userData);
        setSelectedTier((userData.subscriptionTier || "free") as SubscriptionTier);
        setUsername(userData.username || "");
        
        // Load add-on data
        setAddonStorageGB(userData.addons?.extraStorageGB || 0);
        setAddonActive(userData.addons?.active || false);
        
        // Load salepage data
        setSalePageEnabled(userData.salePageSubscription?.status === 'active' || false);
      }
    } catch (err) {
      console.error("Error fetching user info:", err);
    }
  };

  const tiers: { value: SubscriptionTier; label: string; icon: any; color: string; description: string }[] = [
    { value: "free", label: "Free Trial", icon: Zap, color: "gray", description: "14 วัน - 5GB" },
    { value: "pro_monthly", label: "Pro (รายเดือน)", icon: Crown, color: "blue", description: "690฿/เดือน - 20GB" },
    { value: "pro_yearly", label: "Pro (รายปี)", icon: Crown, color: "indigo", description: "6,900฿/ปี - 25GB" },
    { value: "pro_plus_monthly", label: "Pro+ (รายเดือน)", icon: Sparkles, color: "purple", description: "1,590฿/เดือน - 30GB" },
    { value: "pro_plus_yearly", label: "Pro+ (รายปี)", icon: Sparkles, color: "pink", description: "15,900฿/ปี - 35GB" },
  ];

  const handleChangeEmail = async () => {
    if (!email.trim()) {
      setEmailChangeError("กรุณากรอก email เดิม");
      return;
    }

    if (!newEmail.trim()) {
      setEmailChangeError("กรุณากรอก email ใหม่");
      return;
    }

    if (email.trim() === newEmail.trim()) {
      setEmailChangeError("Email ใหม่ต้องไม่เหมือนกับ email เดิม");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      setEmailChangeError("รูปแบบ email ใหม่ไม่ถูกต้อง");
      return;
    }

    setEmailChangeLoading(true);
    setEmailChangeError("");
    setEmailChangeMessage("");

    try {
      // Find user by old email
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setEmailChangeError(`ไม่พบผู้ใช้ที่มี email: ${email}`);
        setEmailChangeLoading(false);
        return;
      }

      const userId = querySnapshot.docs[0].id;

      // Call Cloud Function to change email
      const changeEmailFunction = httpsCallable(functions, "changeUserEmail");
      const result = await changeEmailFunction({
        userId,
        newEmail: newEmail.trim(),
      });

      const response = result.data as { success: boolean; message: string };

      if (response.success) {
        setEmailChangeMessage(
          `✅ เปลี่ยน Email สำเร็จ!\n${response.message}\n\nครูสามารถเข้าสู่ระบบด้วย email ใหม่ได้ทันที`
        );
        setEmail("");
        setNewEmail("");
        setUserInfo(null);
      } else {
        setEmailChangeError("เกิดข้อผิดพลาดในการเปลี่ยน email");
      }
    } catch (err: any) {
      console.error("Error changing email:", err);
      setEmailChangeError(
        `เกิดข้อผิดพลาด: ${err.message || "ไม่สามารถเปลี่ยน email ได้"}`
      );
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!email.trim()) {
      setError("กรุณากรอก email");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      // Find user by email
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError(`ไม่พบผู้ใช้ที่มี email: ${email}`);
        setLoading(false);
        return;
      }

      // Update user
      const userDoc = querySnapshot.docs[0];
      const userId = userDoc.id;
      const userData = userDoc.data();

      const updateData: any = {
        subscriptionTier: selectedTier,
        plan: selectedTier,
        subscriptionId: `sub_${selectedTier}_${Date.now()}`,
      };

      // Set username if provided
      if (username.trim()) {
        updateData.username = username.trim().toLowerCase();
      } else if (!userData.username && selectedTier !== "free") {
        // Auto-generate username from email if not provided
        updateData.username = email.split("@")[0].toLowerCase();
      }

      await updateDoc(doc(db, "users", userId), updateData);

      let urlInfo = "";
      if (selectedTier === "free") {
        urlInfo = `URL: ${window.location.origin}/instructor/${userId}`;
      } else {
        urlInfo = `URL: ${window.location.origin}/${updateData.username || userData.username}`;
      }

      setMessage(
        `✅ อัปเดตสำเร็จ!\n` +
        `Email: ${email}\n` +
        `Tier: ${selectedTier.toUpperCase()}\n` +
        `Username: ${updateData.username || userData.username || "-"}\n` +
        `${urlInfo}`
      );
      
      // Clear form
      setEmail("");
      setUsername("");
    } catch (err: any) {
      console.error("Error updating user:", err);
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddon = async () => {
    if (!email.trim()) {
      setAddonMessage("กรุณากรอก email ก่อน");
      return;
    }

    setAddonLoading(true);
    setAddonMessage("");

    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setAddonMessage(`ไม่พบผู้ใช้ที่มี email: ${email}`);
        setAddonLoading(false);
        return;
      }

      const userId = querySnapshot.docs[0].id;

      await updateDoc(doc(db, "users", userId), {
        "addons.extraStorageGB": addonStorageGB,
        "addons.active": addonActive,
      });

      setAddonMessage(`✅ อัปเดต Add-on สำเร็จ! พื้นที่เพิ่มเติม: ${addonStorageGB} GB (${addonActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'})`);
      
      // Refresh user info
      await fetchUserInfo(email);
    } catch (err: any) {
      console.error("Error updating addon:", err);
      setAddonMessage(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setAddonLoading(false);
    }
  };

  const handleUpdateSalepage = async () => {
    if (!email.trim()) {
      setSalePageMessage("กรุณากรอก email ก่อน");
      return;
    }

    setSalePageLoading(true);
    setSalePageMessage("");

    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setSalePageMessage(`ไม่พบผู้ใช้ที่มี email: ${email}`);
        setSalePageLoading(false);
        return;
      }

      const userId = querySnapshot.docs[0].id;
      const userData = querySnapshot.docs[0].data();

      const updateData: any = {
        "salePageSubscription.status": salePageEnabled ? "active" : "expired",
      };

      // If enabling, set dates
      if (salePageEnabled && (!userData.salePageSubscription || userData.salePageSubscription.status !== 'active')) {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setFullYear(endDate.getFullYear() + 1); // 1 year subscription

        updateData["salePageSubscription.startDate"] = startDate;
        updateData["salePageSubscription.endDate"] = endDate;
        updateData["salePageSubscription.plan"] = "start";
      }

      await updateDoc(doc(db, "users", userId), updateData);

      setSalePageMessage(`✅ อัปเดต Salepage สำเร็จ! สถานะ: ${salePageEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`);
      
      // Refresh user info
      await fetchUserInfo(email);
    } catch (err: any) {
      console.error("Error updating salepage:", err);
      setSalePageMessage(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setSalePageLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-soft py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-textDark">
                  🔧 จัดการ Subscription
                </h1>
                <p className="text-textLight">
                  อัปเดต subscription tier และ username ของผู้ใช้
                </p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>
        </div>

        <Card>
          <div className="space-y-6">
            {/* User Info Display */}
            {userInfo && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                <h3 className="font-bold text-textDark mb-3 flex items-center gap-2">
                  <span className="text-xl">👤</span>
                  ข้อมูลผู้ใช้
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-textLight">ชื่อ:</span>
                    <span className="ml-2 font-semibold text-textDark">{userInfo.name || "ไม่ระบุ"}</span>
                  </div>
                  <div>
                    <span className="text-textLight">Email:</span>
                    <span className="ml-2 font-semibold text-textDark">{userInfo.email}</span>
                  </div>
                  <div>
                    <span className="text-textLight">Role:</span>
                    <span className="ml-2 font-semibold text-textDark">{userInfo.role || "student"}</span>
                  </div>
                  <div>
                    <span className="text-textLight">Current Tier:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                      userInfo.subscriptionTier === "yearly"
                        ? "bg-yellow-100 text-yellow-700"
                        : userInfo.subscriptionTier === "pro_plus"
                        ? "bg-pink-100 text-pink-700"
                        : userInfo.subscriptionTier === "pro"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-700"
                    }`}>
                      {userInfo.subscriptionTier?.toUpperCase() || "FREE"}
                    </span>
                  </div>
                  <div>
                    <span className="text-textLight">Username:</span>
                    <span className="ml-2 font-semibold text-textDark">{userInfo.username || "-"}</span>
                  </div>
                  <div>
                    <span className="text-textLight">User ID:</span>
                    <span className="ml-2 font-mono text-xs text-textDark">{userInfo.uid || "N/A"}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Email Input */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                Email ผู้ใช้ *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setUserInfo(null);
                }}
                onBlur={() => email && fetchUserInfo(email)}
                placeholder="example@email.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
              />
            </div>

            {/* Tier Selection */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-3">
                Subscription Tier *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tiers.map((tier) => {
                  const Icon = tier.icon;
                  const isSelected = selectedTier === tier.value;
                  const colorClasses = {
                    gray: isSelected ? "bg-gray-600 border-gray-700" : "bg-white border-gray-300 hover:border-gray-400",
                    blue: isSelected ? "bg-gradient-to-r from-blue-600 to-cyan-600 border-blue-700" : "bg-white border-blue-300 hover:border-blue-500",
                    indigo: isSelected ? "bg-gradient-to-r from-indigo-600 to-blue-600 border-indigo-700" : "bg-white border-indigo-300 hover:border-indigo-500",
                    purple: isSelected ? "bg-gradient-to-r from-blue-500 to-cyan-500 border-blue-700" : "bg-white border-blue-300 hover:border-blue-500",
                    pink: isSelected ? "bg-gradient-to-r from-fuchsia-600 to-pink-600 border-pink-700" : "bg-white border-pink-300 hover:border-pink-500",
                  };
                  
                  return (
                    <button
                      key={tier.value}
                      onClick={() => setSelectedTier(tier.value)}
                      className={`relative px-4 py-4 rounded-xl font-semibold transition-all border-2 ${
                        colorClasses[tier.color as keyof typeof colorClasses]
                      } ${
                        isSelected ? "text-white shadow-lg scale-105" : "text-textDark"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? "bg-white/20" : "bg-gray-100"
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            isSelected ? "text-white" : `text-${tier.color}-600`
                          }`} />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-bold text-base mb-1">{tier.label}</div>
                          <div className={`text-xs ${
                            isSelected ? "text-white/90" : "text-textLight"
                          }`}>
                            {tier.description}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Username Input */}
            {selectedTier !== "free" && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <label className="block text-sm font-semibold text-textDark mb-2">
                  Username (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  placeholder="username"
                  className="w-full px-4 py-3 border-2 border-blue-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors bg-white"
                />
                <p className="text-xs text-blue-700 mt-2 font-medium">
                  ✨ URL จะเป็น: edumi.co/{username || "username"}
                </p>
              </div>
            )}

            {/* URL Preview */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-blue-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <HardDrive className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-textDark">URL Structure & Storage</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-gray-600 min-w-[120px]">Free Trial:</span>
                  <span className="text-textLight">edumi.co/instructor/[userId] • 5GB</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-blue-600 min-w-[120px]">Pro Monthly:</span>
                  <span className="text-textLight">edumi.co/[username] • 20GB</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-indigo-600 min-w-[120px]">Pro Yearly:</span>
                  <span className="text-textLight">edumi.co/[username] • 25GB</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-blue-600 min-w-[120px]">Pro+ Monthly:</span>
                  <span className="text-textLight">edumi.co/[username] • 30GB + SEO</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-pink-600 min-w-[120px]">Pro+ Yearly:</span>
                  <span className="text-textLight">edumi.co/[username] • 35GB + SEO</span>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="text-red-600 text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {message && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <p className="text-green-700 text-sm whitespace-pre-line font-mono">{message}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleUpdateUser}
              disabled={loading}
              className="w-full bg-gradient-primary text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  กำลังอัปเดต...
                </span>
              ) : (
                "🚀 อัปเดต Subscription"
              )}
            </button>
          </div>
        </Card>

        {/* Email Change Section */}
        <Card className="mt-6">
          <div className="border-b-2 border-gray-200 pb-4 mb-6">
            <h2 className="text-2xl font-bold text-textDark flex items-center gap-2">
              <Mail className="w-7 h-7 text-primary" />
              เปลี่ยน Email ผู้ใช้
            </h2>
            <p className="text-sm text-textLight mt-2">
              สำหรับกรณีที่ครูต้องการเปลี่ยน email เพื่อเข้าถึงคอร์สเดิม
            </p>
          </div>

          <div className="space-y-6">
            {/* Current Email */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                Email เดิม (ปัจจุบัน) *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setUserInfo(null);
                  setEmailChangeMessage("");
                  setEmailChangeError("");
                }}
                onBlur={() => email && fetchUserInfo(email)}
                placeholder="current@email.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
              />
            </div>

            {/* New Email */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                Email ใหม่ *
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailChangeMessage("");
                  setEmailChangeError("");
                }}
                placeholder="new@email.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
              />
            </div>

            {/* Warning Message */}
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
              <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <span>⚠️</span>
                คำเตือน
              </h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Email จะถูกเปลี่ยนทั้งใน Firebase Authentication และ Firestore</li>
                <li>• ครูจะต้องใช้ email ใหม่ในการเข้าสู่ระบบ</li>
                <li>• คอร์สและข้อมูลทั้งหมดจะยังคงอยู่</li>
                <li>• การเปลี่ยนแปลงจะมีผลทันที</li>
              </ul>
            </div>

            {/* Error Message */}
            {emailChangeError && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="text-red-600 text-sm whitespace-pre-line">{emailChangeError}</p>
              </div>
            )}

            {/* Success Message */}
            {emailChangeMessage && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <p className="text-green-700 text-sm whitespace-pre-line font-mono">{emailChangeMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleChangeEmail}
              disabled={emailChangeLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-red-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {emailChangeLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  กำลังเปลี่ยน Email...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Mail className="w-5 h-5" />
                  เปลี่ยน Email
                </span>
              )}
            </button>
          </div>
        </Card>

        {/* Add-on Storage Management */}
        <Card className="mt-6">
          <div className="border-b-2 border-gray-200 pb-4 mb-6">
            <h2 className="text-2xl font-bold text-textDark flex items-center gap-2">
              <Package className="w-7 h-7 text-blue-600" />
              จัดการ Add-on Storage
            </h2>
            <p className="text-sm text-textLight mt-2">
              เพิ่มหรือลดพื้นที่จัดเก็บเพิ่มเติมสำหรับผู้ใช้
            </p>
          </div>

          <div className="space-y-6">
            {/* Current Add-on Info */}
            {userInfo && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-textDark mb-3 flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-blue-600" />
                  Add-on ปัจจุบัน
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-textLight">พื้นที่เพิ่มเติม:</span>
                    <span className="ml-2 font-semibold text-textDark">{userInfo.addons?.extraStorageGB || 0} GB</span>
                  </div>
                  <div>
                    <span className="text-textLight">สถานะ:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                      userInfo.addons?.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                    }`}>
                      {userInfo.addons?.active ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Storage GB Input */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                พื้นที่เพิ่มเติม (GB)
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setAddonStorageGB(Math.max(0, addonStorageGB - 10))}
                  className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  disabled={addonStorageGB <= 0}
                >
                  <Minus className="w-5 h-5 text-textDark" />
                </button>
                <input
                  type="number"
                  value={addonStorageGB}
                  onChange={(e) => setAddonStorageGB(Math.max(0, parseInt(e.target.value) || 0))}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-center font-bold text-xl"
                  min="0"
                  step="10"
                />
                <button
                  onClick={() => setAddonStorageGB(addonStorageGB + 10)}
                  className="p-3 bg-blue-100 hover:bg-blue-200 rounded-xl transition-colors"
                >
                  <Plus className="w-5 h-5 text-blue-600" />
                </button>
              </div>
              <p className="text-xs text-textLight mt-2">
                แนะนำ: 10GB, 25GB, 50GB, 100GB, 200GB
              </p>
            </div>

            {/* Active Toggle */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                สถานะการใช้งาน
              </label>
              <button
                onClick={() => setAddonActive(!addonActive)}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all border-2 ${
                  addonActive
                    ? "bg-green-50 border-green-300 text-green-700"
                    : "bg-gray-50 border-gray-300 text-gray-700"
                }`}
              >
                {addonActive ? (
                  <ToggleRight className="w-8 h-8" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
                <span>{addonActive ? "เปิดใช้งาน" : "ปิดใช้งาน"}</span>
              </button>
            </div>

            {/* Success/Error Message */}
            {addonMessage && (
              <div className={`border-2 rounded-xl p-4 ${
                addonMessage.includes("✅") 
                  ? "bg-green-50 border-green-200" 
                  : "bg-red-50 border-red-200"
              }`}>
                <p className={`text-sm ${
                  addonMessage.includes("✅") ? "text-green-700" : "text-red-600"
                }`}>
                  {addonMessage}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleUpdateAddon}
              disabled={addonLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addonLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  กำลังอัปเดต...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Package className="w-5 h-5" />
                  อัปเดต Add-on Storage
                </span>
              )}
            </button>
          </div>
        </Card>

        {/* Salepage Management */}
        <Card className="mt-6">
          <div className="border-b-2 border-gray-200 pb-4 mb-6">
            <h2 className="text-2xl font-bold text-textDark flex items-center gap-2">
              <FileText className="w-7 h-7 text-emerald-600" />
              จัดการ Salepage Subscription
            </h2>
            <p className="text-sm text-textLight mt-2">
              เปิด/ปิดสิทธิ์การใช้งาน Salepage สำหรับผู้ใช้ Pro+ Yearly
            </p>
          </div>

          <div className="space-y-6">
            {/* Current Salepage Info */}
            {userInfo && (
              <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border-2 border-emerald-200 rounded-xl p-4">
                <h3 className="font-bold text-textDark mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Salepage ปัจจุบัน
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-textLight">สถานะ:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                      userInfo.salePageSubscription?.status === 'active'
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-700"
                    }`}>
                      {userInfo.salePageSubscription?.status === 'active' ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                    </span>
                  </div>
                  <div>
                    <span className="text-textLight">Plan:</span>
                    <span className="ml-2 font-semibold text-textDark">
                      {userInfo.salePageSubscription?.plan?.toUpperCase() || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Enable/Disable Toggle */}
            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                สถานะการใช้งาน Salepage
              </label>
              <button
                onClick={() => setSalePageEnabled(!salePageEnabled)}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all border-2 w-full ${
                  salePageEnabled
                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                    : "bg-gray-50 border-gray-300 text-gray-700"
                }`}
              >
                {salePageEnabled ? (
                  <ToggleRight className="w-8 h-8" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
                <span>{salePageEnabled ? "เปิดใช้งาน Salepage" : "ปิดใช้งาน Salepage"}</span>
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-blue-800 mb-2">ℹ️ ข้อมูล</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Salepage เหมาะสำหรับผู้ใช้ Pro+ Yearly</li>
                <li>• เมื่อเปิดใช้งาน ระบบจะสร้าง subscription 1 ปี</li>
                <li>• ผู้ใช้สามารถสร้าง Salepage ได้ที่ saledi.web.app</li>
                <li>• สามารถใช้ subdomain หรือ custom path ได้</li>
              </ul>
            </div>

            {/* Success/Error Message */}
            {salePageMessage && (
              <div className={`border-2 rounded-xl p-4 ${
                salePageMessage.includes("✅") 
                  ? "bg-green-50 border-green-200" 
                  : "bg-red-50 border-red-200"
              }`}>
                <p className={`text-sm ${
                  salePageMessage.includes("✅") ? "text-green-700" : "text-red-600"
                }`}>
                  {salePageMessage}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleUpdateSalepage}
              disabled={salePageLoading}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {salePageLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  กำลังอัปเดต...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5" />
                  อัปเดต Salepage
                </span>
              )}
            </button>
          </div>
        </Card>

        {/* Quick Update for viitamint@gmail.com */}
        <Card className="mt-6">
          <div className="text-center">
            <h3 className="font-bold text-textDark mb-4">⚡ Quick Update</h3>
            <button
              onClick={() => {
                setEmail("viitamint@gmail.com");
                setSelectedTier("pro_monthly");
                setUsername("viitamint");
              }}
              className="bg-blue-100 text-blue-700 px-6 py-3 rounded-xl font-semibold hover:bg-blue-200 transition-all"
            >
              ตั้งค่า viitamint@gmail.com เป็น Pro Monthly
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
